import { Await, useLoaderData } from "react-router";
import { type FormatDistanceFn, formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { ChevronDown } from "lucide-react";
import { Suspense, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Icon } from "~/components/ui/icon";
import { Skeleton } from "~/components/ui/skeleton";
import { Button } from "~/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { cn } from "~/lib/utils";
import { formatDecimal } from "~/lib/utils/helpers/number";
import { capitalize } from "~/lib/utils/helpers/string";
import { loader } from "~/routes/dashboard";

type RecentSale = {
  id: string;
  completedAt: string;
  bol: string;
  totalPrice: string;
  clientName: string;
  productName: string;
  quantity: string;
  utility: number;
};

const customFormatDistanceToNow: FormatDistanceFn = (token, count, options) => {
  const defaultFormatDistance = es.formatDistance!;
  let result = defaultFormatDistance(token, count, options);

  // Modify the suffix
  if (options?.addSuffix) {
    result = result.replace("hace alrededor de", "hace aprox.");
  }

  return result;
};

const customEsLocale = {
  ...es,
  formatDistance: customFormatDistanceToNow,
};

export function RecentSalesTable() {
  const data = useLoaderData<typeof loader>();
  const [activeStat, setActiveStat] = useState("earnings");

  return (
    <Card className="relative col-span-2 lg:col-span-3 w-full min-h-[450px] md:min-h-0 overflow-hidden">
      {/* 
        NOTE – Need to make this `z-20` higher than the gradient below `z-10`, and
        `relative` to make the gradient appear behind the card header
       */}
      <CardHeader className="z-20 relative flex flex-row justify-between w-full">
        <CardTitle>Ventas recientes</CardTitle>
        <div className="absolute right-4 top-2 w-36">
          <Select value={activeStat} onValueChange={setActiveStat}>
            <SelectTrigger className="h-8 pr-1.5">
              <SelectValue placeholder="Ingresos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="earnings">Ingresos</SelectItem>
              <SelectItem value="liters">Litros</SelectItem>
              <SelectItem value="utility">Utilidad</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>

      <CardContent className="absolute top-0 pt-[70px] h-full w-full max-h-full overflow-scroll">
        <Suspense
          fallback={
            <div className="flex flex-col gap-3 w-full h-full pb-3">
              {Array(15)
                .fill(null)
                .map((_, index) => (
                  <RecentSaleSkeleton key={index} />
                ))}
            </div>
          }
        >
          <Await resolve={data.recentSalesPromise}>
            {(recentSales) => (
              <RecentSalesTableContent
                sales={(recentSales as RecentSale[]) ?? []}
                activeStat={activeStat}
              />
            )}
          </Await>
        </Suspense>
      </CardContent>

      {/* Top and bottom gradients */}
      <div className="z-10 absolute top-0 left-0 right-0 h-[70px] bg-gradient-to-b from-background to-transparent" />
      <span className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-background to-transparent h-20 pointer-events-none" />
    </Card>
  );
}

const RecentSalesTableContent = ({
  sales,
  activeStat,
}: {
  sales: RecentSale[];
  activeStat: "earnings" | "liters" | "utility";
}) => {
  const [visibleCount, setVisibleCount] = useState(25);

  const loadMore = () => {
    setVisibleCount((prevCount) => Math.min(prevCount + 25, sales.length));
  };

  const visibleSales = sales.slice(0, visibleCount);
  const hasMoreToLoad = visibleCount < sales.length;

  const getStatDisplay = (sale: RecentSale) => {
    switch (activeStat) {
      case "earnings":
        return <p>+${formatDecimal(sale.totalPrice)}</p>;
      case "liters":
        return (
          <p>
            {formatDecimal(sale.quantity)}L{" "}
            <span className="text-sm text-muted-foreground">
              ({sale.productName})
            </span>
          </p>
        );
      case "utility":
        // Handle negative utilities correctly (don't add + for negative values)
        const utilityValue = sale.utility;
        const formattedUtility = formatDecimal(String(Math.abs(utilityValue)));
        const prefix = utilityValue >= 0 ? "+$" : "-$";
        return (
          <p className={cn(utilityValue < 0 ? "text-destructive" : "")}>
            {prefix}
            {formattedUtility}
          </p>
        );
      default:
        return <p>+${formatDecimal(sale.totalPrice)}</p>;
    }
  };

  return (
    <div className="flex flex-col gap-3 w-full h-full pb-3">
      {!sales.length ? (
        <div className="flex items-center justify-center w-full h-full">
          <p className="text-sm text-muted-foreground">
            No hay ventas disponibles.
          </p>
        </div>
      ) : (
        <>
          {visibleSales.map((sale, index) => (
            <div
              key={sale.id}
              className={cn(
                "flex justify-between items-center",
                !hasMoreToLoad && visibleSales.length - 1 === index && "pb-6"
              )}
            >
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center min-h-8 min-w-8 rounded-full bg-foreground">
                  <Icon name="scan-barcode" className="text-background" />
                </div>
                <div className="flex flex-col text-sm">
                  <h2>{capitalize(sale.clientName)}</h2>
                  <p className="text-muted-foreground">BOL–{sale.bol}</p>
                </div>
              </div>
              <div className="flex flex-col items-end gap-px">
                <div className="font-medium -mt-0.5">
                  {getStatDisplay(sale)}
                </div>
                <p className="text-small text-muted-foreground text-end leading-3">
                  {formatDistanceToNow(sale.completedAt, {
                    locale: customEsLocale,
                    addSuffix: true,
                  })}
                </p>
              </div>
            </div>
          ))}

          {hasMoreToLoad && (
            <div className="flex justify-center my-2 pb-6">
              <Button
                variant="ghost"
                size="sm"
                className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
                onClick={loadMore}
              >
                Cargar más <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export function RecentSaleSkeleton() {
  return (
    <div className="flex justify-between items-center">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="flex flex-col gap-1">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
      <Skeleton className="h-6 w-28" />
    </div>
  );
}
